import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore react-quill is in the parent dir
import JoditEditor from 'jodit-react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore jodit is in the parent dir
import { IJodit } from 'jodit/esm/types';
import { CheckIcon, CopyIcon } from 'lucide-react';

import { Button } from 'components/Button';
import { ValueType } from 'components/DebounceSelect';
import EmailTemplateSelect from 'components/EmailTemplateSelect';
import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { Textarea } from 'components/Textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/Tooltip';
import EmailRecipientsInput from 'components/input/EmailInput/EmailRecipientsInput';
import { EmailRecipient } from 'components/input/EmailInput/types';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { SET_VALUE_OPTIONS_NO_VALIDATION } from 'constants/SetValueOptionsNoValidation';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import {
  DrumkitPlatform,
  SidebarStateContext,
} from 'contexts/sidebarStateContext';
import useFetchEmailTemplates from 'hooks/useFetchEmailTemplates';
import { useToast } from 'hooks/useToaster';
import { EmailMode, EmailModeType } from 'types/EmailMode';
import { EmailTemplate, TemplateType } from 'types/EmailTemplates';
import { Maybe } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { checkCanReplyInThread } from 'utils/canReplyInThread';
import {
  CopyToClipboardOptions,
  copyToClipboard,
  createRichTextContent,
  isHTMLContent,
} from 'utils/copyToClipboard';
import {
  hasDuplicateEmails,
  normalizeEmail,
  sanitizeEmails,
} from 'utils/emails';
import { getJoditEditorConfig, nl2br } from 'utils/getJoditEditorHelpers';
import { mapEmailTemplatesToSelectOptions } from 'utils/mapEmailTemplatesToSelectOptions';

import EmailModeToggle from './EmailModeToggle';
import ShowCCBCCButtons from './ShowCCBCCButtons';
import {
  getDefaultFromAddress,
  getDefaultReplyRecipients,
  recipientsContainPrimary,
} from './helpers';
import {
  EmailComposerConfig,
  EmailComposerFormData,
  EmailModeParticipants,
  EmailParticipants,
} from './types';

interface EmailComposerProps {
  config: EmailComposerConfig;
  headerComponent?: React.ReactNode;
  initialRecipients?: EmailRecipient[];
  // Extra To suggestion chips for new-email mode (merged with initialRecipients).
  aiSuggestedTo?: EmailRecipient[];
  aiSuggestedCc?: EmailRecipient[];
  aiSuggestedBcc?: EmailRecipient[];
  getDefaultSubject?: (template?: EmailTemplate) => string;
  getDefaultEmailBody?: (template?: EmailTemplate) => string;
  onTemplateSelect?: (template: EmailTemplate) => void;
  // Custom mode selector component (replaces default EmailModeToggle)
  modeSelectorComponent?: React.ReactNode;
  // Use external form context instead of creating own FormProvider
  useExternalFormContext?: boolean;
  // External form control (required if useExternalFormContext is true)
  externalControl?: any;
}

const EmailComposer = ({
  config,
  headerComponent,
  initialRecipients = [],
  aiSuggestedTo: aiSuggestedToProp = [],
  aiSuggestedCc = [],
  aiSuggestedBcc = [],
  getDefaultSubject,
  getDefaultEmailBody,
  onTemplateSelect,
  modeSelectorComponent,
  useExternalFormContext = false,
  externalControl,
}: EmailComposerProps) => {
  const { toast } = useToast();

  const {
    currentState: {
      threadId,
      threadItemId,
      threadReferences,
      inReplyTo,
      drumkitPlatform,
      inboxEmailAddress,
      threadFrom,
      threadTo,
      threadCc,
    },
  } = useContext(SidebarStateContext);

  const emailBodyJoditRef = useRef<Maybe<IJodit>>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [showCcInput, setShowCcInput] = useState(false);
  const [showBccInput, setShowBccInput] = useState(false);
  const [emailMode, setEmailMode] = useState<EmailModeType>(EmailMode.Reply);
  const [hasCopiedEmailBody, setHasCopiedEmailBody] = useState(false);
  const copyEmailBodyTimeoutRef = useRef<Maybe<NodeJS.Timeout>>(null);
  const templateInjectedCcRef = useRef<string[]>([]);

  const emptyEmailParticipants: EmailParticipants = {
    to: [],
    cc: [],
    bcc: [],
  };

  const getInitialEmailParticipants = (): EmailModeParticipants => ({
    [EmailMode.New]: {
      to: initialRecipients.map((r) => r.email),
      cc: [],
      bcc: [],
    },
    [EmailMode.Reply]: emptyEmailParticipants,
    [EmailMode.ReplyToSubject]: emptyEmailParticipants,
  });

  // Persist participant data (from, to, cc, bcc) in state for when user toggles between email modes.
  // Initial filter uses only inbox (sendAs may not be loaded yet); effect below
  // applies sendAs filter when it loads without overriding user edits.
  const [savedParticipants, setSavedParticipants] =
    useState<EmailModeParticipants>(getInitialEmailParticipants);

  const handleEmailModeChange = (newMode: EmailModeType) => {
    if (!newMode) {
      return;
    }

    // Save current form values and switch mode in one update
    const currentValues = getValues();
    const currentFrom = getValues(getFieldName('from'));
    setSavedParticipants((prev) => {
      const updatedParticipants = {
        ...prev,
        [emailMode]: {
          from: currentFrom ?? undefined,
          to: currentValues.to || [],
          cc: currentValues.cc || [],
          bcc: currentValues.bcc || [],
        },
      };

      // Restore saved values for the new mode
      const savedValues = updatedParticipants[newMode];

      // For reply mode, if no saved values exist, populate with thread participants
      if (
        newMode === EmailMode.Reply &&
        (!savedValues || savedValues.to.length === 0)
      ) {
        const threadParticipants = [
          ...(threadFrom ? [threadFrom] : []),
          ...threadTo,
          ...threadCc,
        ];
        // Remove duplicates, current user's email, and user's send-as addresses
        const uniqueParticipants = getDefaultReplyRecipients(
          threadParticipants,
          inboxEmailAddress ?? '',
          config.sendAsAddresses ?? []
        );

        setValue(
          getFieldName('to'),
          uniqueParticipants,
          SET_VALUE_OPTIONS_NO_VALIDATION
        );
        setValue(getFieldName('cc'), [], SET_VALUE_OPTIONS_NO_VALIDATION);
        setValue(getFieldName('bcc'), [], SET_VALUE_OPTIONS_NO_VALIDATION);
      } else if (savedValues) {
        // Restore previously saved values
        setValue(
          getFieldName('to'),
          savedValues.to,
          SET_VALUE_OPTIONS_NO_VALIDATION
        );
        setValue(
          getFieldName('cc'),
          savedValues.cc,
          SET_VALUE_OPTIONS_NO_VALIDATION
        );
        setValue(
          getFieldName('bcc'),
          savedValues.bcc,
          SET_VALUE_OPTIONS_NO_VALIDATION
        );
        if (savedValues.from) {
          setValue(
            getFieldName('from'),
            normalizeEmail(savedValues.from),
            SET_VALUE_OPTIONS_NO_VALIDATION
          );
        }
      }

      return updatedParticipants;
    });

    // Switch mode
    setEmailMode(newMode);

    // Only set subject when switching to New mode
    if (newMode === EmailMode.New) {
      // Get the currently selected template's subject
      const currentTemplateId = currentValues.emailTemplateId;
      const selectedTemplate = findTemplateByValue(currentTemplateId);
      const subjectToUse = getDefaultSubject
        ? getDefaultSubject(selectedTemplate || undefined)
        : selectedTemplate?.subject;

      if (subjectToUse) {
        setValue(
          getFieldName('subject'),
          subjectToUse,
          SET_VALUE_OPTIONS_NO_VALIDATION
        );
      }
    }
  };

  // Helper to get field name with prefix and mapping
  const getFieldName = (fieldName: string): string => {
    // Apply field name mapping if provided
    const mappedFieldName =
      config.fieldNameMap?.[fieldName as keyof typeof config.fieldNameMap] ||
      fieldName;
    // Apply prefix if provided
    return config.fieldNamePrefix
      ? `${config.fieldNamePrefix}.${mappedFieldName}`
      : mappedFieldName;
  };

  const formMethods = useForm<EmailComposerFormData>({
    mode: 'onChange',
    defaultValues: {
      from: savedParticipants[emailMode].from ?? inboxEmailAddress ?? '',
      to: savedParticipants[emailMode].to,
      cc: savedParticipants[emailMode].cc,
      bcc: savedParticipants[emailMode].bcc,
      subject: '',
      emailBody: '',
      emailTemplateId: '',
    },
  });

  // For external form context, use useFormContext
  const externalFormContext = useExternalFormContext ? useFormContext() : null;

  // Use external form context if provided, otherwise use internal form
  const setValue =
    useExternalFormContext && externalFormContext
      ? (externalFormContext.setValue as any)
      : formMethods.setValue;
  const getValues =
    useExternalFormContext && externalFormContext
      ? (externalFormContext.getValues as any)
      : formMethods.getValues;
  const formState =
    useExternalFormContext && externalFormContext
      ? externalFormContext.formState
      : formMethods.formState;
  const watch =
    useExternalFormContext && externalFormContext
      ? (externalFormContext.watch as any)
      : formMethods.watch;
  const control = useExternalFormContext
    ? externalControl || formMethods.control
    : formMethods.control;
  const handleSubmit =
    useExternalFormContext && externalFormContext
      ? (externalFormContext.handleSubmit as any)
      : formMethods.handleSubmit;
  const emailBodyValue = watch(getFieldName('emailBody'));

  // When using external form context, read emailMode from the form
  // Otherwise use internal state
  const externalEmailMode =
    useExternalFormContext && externalFormContext
      ? (watch(getFieldName('emailMode')) as EmailModeType)
      : null;
  const currentEmailMode =
    useExternalFormContext && externalEmailMode !== null
      ? externalEmailMode
      : emailMode;

  // Exclude shared email addresses from reply mode recipients, similar to the way we exclude the user's own email address in the initial recipients
  useEffect(() => {
    const toField = getFieldName('to');
    if (
      formState.dirtyFields?.[toField as keyof typeof formState.dirtyFields]
    ) {
      return;
    }
    setSavedParticipants((prev) => {
      const newParticipants = {
        ...prev,
        [EmailMode.Reply]: {
          ...prev[EmailMode.Reply],
          to: getDefaultReplyRecipients(
            prev[EmailMode.Reply].to,
            inboxEmailAddress,
            config.sendAsAddresses ?? []
          ),
        },
      };
      return newParticipants;
    });
  }, [config.sendAsAddresses, inboxEmailAddress]);

  // Sync internal emailMode state when using external form context
  useEffect(() => {
    if (
      useExternalFormContext &&
      externalEmailMode !== null &&
      externalEmailMode !== emailMode
    ) {
      setEmailMode(externalEmailMode);
    }
  }, [useExternalFormContext, externalEmailMode, emailMode]);

  const aiHintTooltip = useMemo(
    () =>
      currentEmailMode === EmailMode.New
        ? 'Options filled based on TMS load info.'
        : "Recipients filled based on existing email thread's participants.",
    [currentEmailMode]
  );

  // AI Suggested options for email fields
  const aiSuggestedTo: EmailRecipient[] = useMemo(() => {
    // For reply mode, suggest all thread participants except current user
    if (currentEmailMode === EmailMode.Reply) {
      const allParticipants = [
        ...(threadFrom ? [threadFrom] : []),
        ...threadTo,
        ...threadCc,
      ];
      const uniqueParticipants = getDefaultReplyRecipients(
        allParticipants,
        inboxEmailAddress,
        config.sendAsAddresses ?? []
      );
      return uniqueParticipants.map((email) => ({
        email,
        name: email,
      }));
    }

    // For new email mode, merge prefilled To with optional learned suggestions.
    // Dedupe case-insensitively but keep original email casing so pills still
    // match form To values (EmailRecipientsInput uses strict equality).
    const merged: EmailRecipient[] = [];
    const seen = new Set<string>();
    for (const recipient of [...initialRecipients, ...aiSuggestedToProp]) {
      const trimmed = recipient.email.trim();
      const key = trimmed.toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      merged.push({ ...recipient, email: trimmed });
    }
    return merged;
  }, [
    currentEmailMode,
    initialRecipients,
    aiSuggestedToProp,
    threadFrom,
    threadTo,
    threadCc,
    inboxEmailAddress,
    config.sendAsAddresses,
  ]);

  // Platform-aware validation: check if we have the required threading fields
  const canReplyInThread = checkCanReplyInThread(
    drumkitPlatform,
    threadId,
    threadItemId,
    threadReferences,
    inReplyTo
  );

  const { emailTemplates } = useFetchEmailTemplates(
    config.loadID,
    config.templateType as TemplateType,
    config.stopType
  );

  // Extract templates using the provided extractor function
  const emailTemplatesList: EmailTemplate[] = useMemo(() => {
    if (!emailTemplates) {
      return [];
    }
    return config.extractTemplates(emailTemplates);
  }, [emailTemplates, config.extractTemplates]);

  const findTemplateByValue = (
    value?: string | number
  ): Maybe<EmailTemplate> => {
    if (value === undefined || value === null) {
      return null;
    }

    const templateIndex =
      typeof value === 'string' && value.startsWith('template-')
        ? Number(value.replace('template-', ''))
        : Number(value);

    if (Number.isNaN(templateIndex)) {
      return null;
    }

    return emailTemplatesList[templateIndex] || null;
  };

  const handleEmailTemplateSelection = (selectedTemplate: ValueType) => {
    if (!selectedTemplate) {
      return;
    }

    const emailTemplate =
      findTemplateByValue(selectedTemplate.value) ||
      ((selectedTemplate as EmailTemplate) ?? null);

    if (!emailTemplate) {
      return;
    }

    const emailBody = getDefaultEmailBody
      ? getDefaultEmailBody(emailTemplate)
      : nl2br(emailTemplate.body);

    setValue(
      getFieldName('emailBody'),
      emailBody,
      SET_VALUE_OPTIONS_NO_VALIDATION
    );

    if (currentEmailMode === EmailMode.New && emailTemplate.subject) {
      const subject = getDefaultSubject
        ? getDefaultSubject(emailTemplate)
        : emailTemplate.subject;
      if (subject) {
        setValue(
          getFieldName('subject'),
          subject,
          SET_VALUE_OPTIONS_NO_VALIDATION
        );
      }
    }

    // Filter out empty strings from CC before setting
    const validCcEmails = emailTemplate.cc?.filter(
      (email) => email && email.trim() !== ''
    );

    // Remove cc emails from prev template when switching to new email template without affecting user input cc emails
    const currentCc = sanitizeEmails(getValues(getFieldName('cc')));
    const previousTemplateEmails = templateInjectedCcRef.current.map((email) =>
      email.trim()
    );
    const previousTemplateSet = new Set(
      previousTemplateEmails.map((email) => normalizeEmail(email))
    );
    const userOnlyCc = currentCc.filter(
      (email) => !previousTemplateSet.has(normalizeEmail(email))
    );
    const templateCc = sanitizeEmails(validCcEmails);
    const mergedCc = [...userOnlyCc];

    templateCc.forEach((email) => {
      const normalized = normalizeEmail(email);
      const exists = mergedCc.some(
        (existing) => normalizeEmail(existing) === normalized
      );
      if (!exists) {
        mergedCc.push(email);
      }
    });

    if (!hasDuplicateEmails(mergedCc, currentCc)) {
      setValue(getFieldName('cc'), mergedCc, SET_VALUE_OPTIONS_NO_VALIDATION);
    }

    templateInjectedCcRef.current = templateCc;

    if (onTemplateSelect) {
      onTemplateSelect(emailTemplate);
    }
  };

  // Auto-select initial template when templates load. Prefer the configured
  // default template (e.g. the backend priority/"AI filled" template) and fall
  // back to the first available option. Skips once the user has picked a template.
  useEffect(() => {
    if (emailTemplatesList.length === 0) {
      return;
    }
    if (formState.dirtyFields.emailTemplateId) {
      return;
    }

    const allOptions = mapEmailTemplatesToSelectOptions(
      emailTemplatesList
    ).flatMap((group) => group.options ?? []);
    if (allOptions.length === 0) {
      return;
    }

    const initialTemplate =
      (config.defaultTemplateId != null &&
        allOptions.find(
          (option) =>
            (option as unknown as EmailTemplate).id === config.defaultTemplateId
        )) ||
      allOptions[0];

    handleEmailTemplateSelection(initialTemplate);
    setValue(
      getFieldName('emailTemplateId'),
      initialTemplate.value,
      SET_VALUE_OPTIONS_NO_VALIDATION
    );
  }, [
    emailTemplatesList,
    config.defaultTemplateId,
    setValue,
    formState.dirtyFields,
  ]);

  // Handle email mode changes - restore saved participants when mode changes only.
  // Do not depend on savedParticipants so we don't re-run when other effects update
  // savedParticipants (e.g. replyRecipientsExcludeShared, initReplyModeParticipants).
  useEffect(() => {
    if (useExternalFormContext) {
      return;
    }
    const savedValues = savedParticipants[emailMode];
    setValue(
      getFieldName('to'),
      savedValues.to,
      SET_VALUE_OPTIONS_NO_VALIDATION
    );
    setValue(
      getFieldName('cc'),
      savedValues.cc,
      SET_VALUE_OPTIONS_NO_VALIDATION
    );
    setValue(
      getFieldName('bcc'),
      savedValues.bcc,
      SET_VALUE_OPTIONS_NO_VALIDATION
    );
    if (savedValues.from) {
      setValue(
        getFieldName('from'),
        normalizeEmail(savedValues.from),
        SET_VALUE_OPTIONS_NO_VALIDATION
      );
    }
  }, [emailMode, useExternalFormContext]);

  // Automatically switch from Reply to New mode if threading becomes unavailable
  useEffect(() => {
    if (emailMode === EmailMode.Reply && !canReplyInThread) {
      setEmailMode(EmailMode.New);
    }
  }, [emailMode, canReplyInThread]);

  const fromReadOnly =
    (currentEmailMode === EmailMode.Reply ||
      currentEmailMode === EmailMode.ReplyToSubject) &&
    !config.isLoggedInUserSharedMailbox &&
    !!inboxEmailAddress &&
    recipientsContainPrimary(threadTo ?? [], threadCc ?? [], inboxEmailAddress);

  // On Reply/ReplyToSubject: if fromReadOnly (personal thread), lock From value to primary only; else use saved or default From.
  // Depend on savedFrom (primitive) instead of full savedParticipants to avoid re-running when
  // other effects update savedParticipants (e.g. replyRecipientsExcludeShared only updates to).
  const savedFrom = savedParticipants[currentEmailMode]?.from ?? '';

  useEffect(() => {
    if (
      currentEmailMode !== EmailMode.Reply &&
      currentEmailMode !== EmailMode.ReplyToSubject
    ) {
      return;
    }
    const sendAs = config.sendAsAddresses ?? [];
    const toList = threadTo ?? [];
    const ccList = threadCc ?? [];
    const fromValue = fromReadOnly
      ? inboxEmailAddress
      : (savedParticipants[currentEmailMode]?.from ??
        getDefaultFromAddress(toList, ccList, inboxEmailAddress ?? '', sendAs));
    if (fromValue) {
      setValue(
        getFieldName('from'),
        normalizeEmail(fromValue),
        SET_VALUE_OPTIONS_NO_VALIDATION
      );
    }
  }, [
    currentEmailMode,
    fromReadOnly,
    inboxEmailAddress,
    threadTo,
    threadCc,
    config.sendAsAddresses,
    savedFrom,
  ]);

  // Initialize reply mode participants when component loads or thread data becomes available.
  const savedReplyToLength =
    savedParticipants[EmailMode.Reply]?.to?.length ?? 0;

  useEffect(() => {
    if (useExternalFormContext) {
      return;
    }
    if (
      emailMode === EmailMode.Reply &&
      (threadFrom || threadTo.length > 0 || threadCc.length > 0)
    ) {
      const savedValues = savedParticipants[EmailMode.Reply];

      if (savedValues.to.length === 0) {
        const threadParticipants = [
          ...(threadFrom ? [threadFrom] : []),
          ...threadTo,
          ...threadCc,
        ];
        const uniqueParticipants = getDefaultReplyRecipients(
          threadParticipants,
          inboxEmailAddress ?? '',
          config.sendAsAddresses ?? []
        );

        setValue(
          getFieldName('to'),
          uniqueParticipants,
          SET_VALUE_OPTIONS_NO_VALIDATION
        );
        setValue(getFieldName('cc'), [], SET_VALUE_OPTIONS_NO_VALIDATION);
        setValue(getFieldName('bcc'), [], SET_VALUE_OPTIONS_NO_VALIDATION);

        setSavedParticipants((prev) => ({
          ...prev,
          [EmailMode.Reply]: {
            ...prev[EmailMode.Reply],
            to: uniqueParticipants,
            cc: [],
            bcc: [],
          },
        }));
      }
    }
  }, [
    emailMode,
    threadFrom,
    threadTo,
    threadCc,
    inboxEmailAddress,
    setValue,
    savedReplyToLength,
    useExternalFormContext,
    config.sendAsAddresses,
  ]);

  useEffect(() => {
    return () => {
      if (copyEmailBodyTimeoutRef.current) {
        clearTimeout(copyEmailBodyTimeoutRef.current);
      }
    };
  }, []);

  const onSubmit = async (data: EmailComposerFormData) => {
    // If using external form context, don't handle submission here
    if (useExternalFormContext) {
      return;
    }

    if (!config.loadID) {
      toast({
        description: 'Load ID is missing. Please try again.',
        variant: 'destructive',
      });
      return;
    }

    if (!config.sendEmail) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Get the selected template ID from form
      const selectedTemplateId = data.emailTemplateId
        ? emailTemplatesList.find(
            (_, index) => `template-${index}` === data.emailTemplateId
          )?.id
        : undefined;

      const result = await config.sendEmail({
        loadID: config.loadID,
        recipients: data.to,
        cc: data.cc,
        bcc: data.bcc,
        subject: data.subject,
        emailBody: data.emailBody,
        emailTemplateId: selectedTemplateId,
        from: data.from,
      });

      if (result.isOk()) {
        toast({
          description: config.successMessage || 'Email sent successfully!',
          variant: 'success',
        });
        if (config.onEmailSent) {
          config.onEmailSent();
        }
        return;
      }

      toast({
        description:
          result.error.message || config.errorMessage || 'Failed to send email',
        variant: 'destructive',
      });
    } catch {
      toast({
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onReplyToThread = async (data: EmailComposerFormData) => {
    // If using external form context, don't handle submission here
    if (useExternalFormContext) {
      return;
    }

    if (!config.loadID) {
      toast({
        description: 'Load ID is missing. Please try again.',
        variant: 'destructive',
      });
      return;
    }

    if (!config.sendEmail) {
      return;
    }

    // Platform-specific validation
    if (
      drumkitPlatform === DrumkitPlatform.Outlook ||
      drumkitPlatform === DrumkitPlatform.Front
    ) {
      if (!threadId && !threadItemId) {
        toast({
          description:
            'Thread ID is missing. Please open this from an email thread.',
          variant: 'destructive',
        });
        return;
      }
    } else if (drumkitPlatform === DrumkitPlatform.Sidepanel) {
      if (!threadReferences && (!inReplyTo || inReplyTo.length === 0)) {
        toast({
          description:
            'Thread information is missing. Please open this from an email thread.',
          variant: 'destructive',
        });
        return;
      }
    }

    setIsSubmittingReply(true);

    try {
      // Get the selected template ID from form
      const selectedTemplateId = data.emailTemplateId
        ? emailTemplatesList.find(
            (_, index) => `template-${index}` === data.emailTemplateId
          )?.id
        : undefined;

      const result = await config.sendEmail({
        loadID: config.loadID,
        recipients: data.to,
        cc: data.cc,
        bcc: data.bcc,
        subject: undefined, // Don't send subject when replying, backend thread logic handles it
        emailBody: data.emailBody,
        emailTemplateId: selectedTemplateId,
        replyInThread: true,
        // Outlook/Front fields
        threadID: threadId,
        threadItemID: threadItemId,
        // Gmail fields
        threadReferences: threadReferences,
        inReplyTo: inReplyTo,
        from: data.from,
      });

      if (result.isOk()) {
        toast({
          description: config.replySuccessMessage || 'Reply sent successfully!',
          variant: 'success',
        });
        if (config.onEmailSent) {
          config.onEmailSent();
        }
        return;
      }

      toast({
        description:
          result.error.message || config.errorMessage || 'Failed to send reply',
        variant: 'destructive',
      });
    } catch {
      toast({
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const handleCopyEmailBody = async () => {
    const currentBody = getValues(getFieldName('emailBody'));
    if (!currentBody) {
      toast({
        description: 'No email body to copy.',
        variant: 'destructive',
      });
      return;
    }

    // Call copy email function if provided
    if (config.copyEmail) {
      await config.copyEmail?.({
        emailBody: currentBody,
        subject: getValues(getFieldName('subject')),
        freightTrackingId: config.freightTrackingId,
      });
    }

    const copyToClipboardOptions: CopyToClipboardOptions = {
      toastMessage: 'Email body copied to clipboard',
      toastVariant: 'default',
      wrapInHTML: true,
      joditEditorRef: emailBodyJoditRef,
      toastDuration: 3000,
    };

    const success = await copyToClipboard(
      isHTMLContent(currentBody)
        ? createRichTextContent(currentBody)
        : currentBody,
      copyToClipboardOptions
    );

    if (success) {
      setHasCopiedEmailBody(true);
      if (copyEmailBodyTimeoutRef.current) {
        clearTimeout(copyEmailBodyTimeoutRef.current);
      }
      copyEmailBodyTimeoutRef.current = setTimeout(() => {
        setHasCopiedEmailBody(false);
        copyEmailBodyTimeoutRef.current = null;
      }, 1000);

      if (config.onEmailCopied) {
        config.onEmailCopied();
      }
    }
  };

  const showSubjectForNewEmailOnly =
    config.showSubjectForNewEmailOnly !== false; // Default to true

  const renderFormFields = () => (
    <Flex direction='col' gap='md' className='w-full'>
      <Label name='from' required showAIHint={false}>
        From
      </Label>
      <Controller
        name={getFieldName('from')}
        control={control}
        render={({ field }) => {
          const addresses =
            config.sendAsAddresses && config.sendAsAddresses.length > 0
              ? config.sendAsAddresses
              : [inboxEmailAddress];

          const inboxLower = (inboxEmailAddress || '').toLowerCase();
          const sendAsOptions =
            addresses.length > 0
              ? addresses.includes(inboxLower)
                ? addresses
                : [inboxLower, ...addresses]
              : [];

          const fromDisabled = config.disabled || fromReadOnly;
          return (
            <Select
              value={field.value || ''}
              onValueChange={field.onChange}
              disabled={fromDisabled}
              className='-mt-2'
            >
              <SelectTrigger className='-mt-2'>
                <SelectValue placeholder='Email address' />
              </SelectTrigger>
              <SelectContent>
                {sendAsOptions.map((addr) => (
                  <SelectItem key={addr} value={addr}>
                    {addr}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }}
      />

      <Controller
        name={getFieldName('to')}
        control={control}
        rules={{
          validate: (value) => {
            if (
              config.requireToEmail !== false &&
              (!value || value.length === 0)
            ) {
              return 'To email is required';
            }
            return true;
          },
        }}
        render={({ field, fieldState }) => (
          <EmailRecipientsInput
            name='to'
            label='To'
            value={field.value}
            onChange={field.onChange}
            customOptions={aiSuggestedTo}
            placeholder='Enter recipient emails'
            required={true}
            allowMultiple={true}
            error={fieldState.error?.message}
            aiHintTooltip={aiHintTooltip}
            disabled={isSubmitting || isSubmittingReply || config.disabled}
          />
        )}
      />

      <ShowCCBCCButtons
        showCcInput={showCcInput}
        showBccInput={showBccInput}
        onToggleCc={() => setShowCcInput(!showCcInput)}
        onToggleBcc={() => setShowBccInput(!showBccInput)}
      />

      {showCcInput && (
        <Flex direction='col' gap='none' className='w-full'>
          <Controller
            name={getFieldName('cc')}
            control={control}
            render={({ field, fieldState }) => (
              <EmailRecipientsInput
                name='cc'
                label='Cc:'
                value={field.value}
                onChange={field.onChange}
                customOptions={aiSuggestedCc}
                placeholder='Type or paste email(s) here'
                allowMultiple={true}
                error={fieldState.error?.message}
                disabled={config.disabled}
              />
            )}
          />
        </Flex>
      )}

      {showBccInput && (
        <Flex direction='col' gap='none' className='w-full'>
          <Controller
            name={getFieldName('bcc')}
            control={control}
            render={({ field, fieldState }) => (
              <EmailRecipientsInput
                name='bcc'
                label='Bcc:'
                value={field.value}
                onChange={field.onChange}
                customOptions={aiSuggestedBcc}
                placeholder='Type or paste email(s) here'
                allowMultiple={true}
                error={fieldState.error?.message}
                disabled={config.disabled}
              />
            )}
          />
        </Flex>
      )}

      {emailTemplatesList.length > 1 && (
        <EmailTemplateSelect
          emailTemplates={emailTemplatesList}
          onTemplateSelect={handleEmailTemplateSelection}
          label='Email Template'
          placeholder='Select an email template'
        />
      )}

      {currentEmailMode === EmailMode.New &&
        (showSubjectForNewEmailOnly || config.requireSubject) && (
          <Flex direction='col' gap='xs' className='w-full'>
            <Label name='subject' required={config.requireSubject}>
              Subject
            </Label>
            <Controller
              name={getFieldName('subject')}
              control={control}
              rules={{
                required: config.requireSubject ? 'Subject is required' : false,
                minLength: config.requireSubject
                  ? {
                      value: 5,
                      message: 'Subject must be at least 5 characters',
                    }
                  : undefined,
              }}
              render={({ field }) => (
                <Textarea
                  className='min-h-[20px]'
                  value={field.value}
                  onChange={field.onChange}
                  rows={Math.max(2, field.value.split('\n').length || 1)}
                  disabled={config.disabled}
                />
              )}
            />
            <ErrorMessage
              errors={formState.errors}
              name='subject'
              render={({ message }) => (
                <Typography variant='body-xs' className='text-error-500'>
                  {message}
                </Typography>
              )}
            />
          </Flex>
        )}

      <Flex direction='col' gap='xs' className='w-full'>
        <Flex align='center' justify='between' gap='sm' className='w-full'>
          <Label name='emailBody' required>
            Email Body
          </Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type='button'
                  variant='ghost'
                  className='h-6 w-6 p-0 border-none ml-2 transition-colors hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed shrink-0'
                  onClick={handleCopyEmailBody}
                  disabled={!emailBodyValue || config.disabled}
                  aria-label='Copy email body'
                  buttonNamePosthog={
                    ButtonNamePosthog.EmailSchedulingCopyEmailDraft
                  }
                >
                  {hasCopiedEmailBody ? (
                    <CheckIcon className='h-4 w-4 text-success-600' />
                  ) : (
                    <CopyIcon className='h-4 w-4 text-neutral-600' />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side='top' className='rounded-md mr-2'>
                {hasCopiedEmailBody ? 'Copied!' : 'Copy email body'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </Flex>
        <Controller
          name={getFieldName('emailBody')}
          control={control}
          rules={{
            required: 'Email body is required',
            minLength: {
              value: 10,
              message: 'Email body must be at least 10 characters',
            },
          }}
          render={({ field }) => (
            <div className='w-full overflow-hidden'>
              <JoditEditor
                value={field.value}
                config={getJoditEditorConfig({
                  refToUpdateAfterInit: emailBodyJoditRef,
                })}
                onBlur={(e: any) => field.onChange(e)}
              />
            </div>
          )}
        />
        <ErrorMessage
          errors={formState.errors}
          name='emailBody'
          render={({ message }) => (
            <Typography variant='body-xs' className='text-error-500'>
              {message}
            </Typography>
          )}
        />
      </Flex>

      {!useExternalFormContext && (
        <Flex direction='col' gap='sm' className='mt-2 w-full'>
          {currentEmailMode === EmailMode.New ? (
            <Button
              type='submit'
              className='w-full text-sm'
              disabled={isSubmitting || isSubmittingReply || config.disabled}
              buttonNamePosthog={
                ButtonNamePosthog.EmailSchedulingCopyEmailDraft
              }
            >
              {isSubmitting ? (
                <>
                  <ButtonLoader />
                  Sending...
                </>
              ) : (
                'Send Email'
              )}
            </Button>
          ) : (
            canReplyInThread && (
              <Button
                type='button'
                className='w-full text-sm'
                disabled={isSubmitting || isSubmittingReply || config.disabled}
                onClick={handleSubmit(onReplyToThread)}
                buttonNamePosthog={
                  ButtonNamePosthog.EmailSchedulingCopyEmailDraft
                }
              >
                {isSubmittingReply ? (
                  <>
                    <ButtonLoader />
                    Sending Reply...
                  </>
                ) : (
                  'Reply to Thread'
                )}
              </Button>
            )
          )}
        </Flex>
      )}
    </Flex>
  );

  const formContent = (
    <div className='w-full mx-auto py-4'>
      {headerComponent}

      {modeSelectorComponent
        ? modeSelectorComponent
        : canReplyInThread && (
            <EmailModeToggle
              value={emailMode}
              onValueChange={handleEmailModeChange}
              canReplyInThread={canReplyInThread}
            />
          )}

      {useExternalFormContext ? (
        renderFormFields()
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={(e) => {
            if (
              e.key === 'Enter' &&
              (e.target as HTMLElement).tagName === 'INPUT'
            ) {
              e.preventDefault();
            }
          }}
          className='w-full'
        >
          {renderFormFields()}
        </form>
      )}
    </div>
  );

  // Wrap in FormProvider only if not using external form context
  if (useExternalFormContext) {
    return formContent;
  }

  return (
    <ExtendedFormProvider aiDefaultValues={true}>
      <FormProvider {...formMethods}>{formContent}</FormProvider>
    </ExtendedFormProvider>
  );
};

export default EmailComposer;
